<template>
  <div class="aml-search-root">
  <v-fade-transition>
  <v-container fluid class="aml-dash pa-5">

    <!-- ══ Header ══ -->
    <div class="d-flex align-center justify-space-between mb-5">
      <div class="d-flex align-center">
        <button class="aml-back-btn mr-3" @click="$router.push({ name: 'watchlist-dashboard' })">
          <v-icon small>mdi-arrow-left</v-icon>
        </button>
        <div>
          <div class="aml-overline mb-1">Watchlist Screening</div>
          <h2 class="aml-title">{{ searchMode === 'mass' ? 'Mass Screening' : 'Search' }}</h2>
        </div>
      </div>
      <div class="d-flex align-center" style="gap:8px">
        <v-btn depressed small class="aml-btn-ghost" @click="$router.push({ name: 'watchlist-search-reports' })">
          <v-icon small left>mdi-file-document-outline</v-icon>Search reports
        </v-btn>
        <v-btn-toggle v-model="searchMode" mandatory dense>
          <v-btn value="single" small depressed>
            <v-icon small left>mdi-account-search-outline</v-icon>Single
          </v-btn>
          <v-btn value="mass" small depressed>
            <v-icon small left>mdi-account-group-outline</v-icon>Mass
          </v-btn>
        </v-btn-toggle>
      </div>
    </div>

    <v-row dense>

      <!-- ══ Search Form ══ -->
      <v-col cols="12" md="5">
        <div class="aml-card pa-4">

          <!-- ── Single Search ── -->
          <template v-if="searchMode === 'single'">
            <div class="aml-section-label mb-3">
              Account
              <span style="font-weight:400;color:#aaa;font-size:10px;text-transform:none;letter-spacing:0;margin-left:4px">— optional, link to create a flag on match</span>
            </div>

            <!-- Selected account pill -->
            <div v-if="selectedAccount" class="aml-person-pill mb-4">
              <div class="aml-person-avatar">{{ avatarInitials(selectedAccount.personName) }}</div>
              <div class="flex-grow-1">
                <div class="aml-person-name">{{ selectedAccount.personName }}</div>
                <div class="aml-person-sub">{{ selectedAccount.mainAccountNumber }}<span v-if="selectedAccount.trn"> · TRN: {{ selectedAccount.trn }}</span></div>
              </div>
              <v-btn icon small @click="clearAccount"><v-icon small>mdi-close-circle</v-icon></v-btn>
            </div>

            <template v-else>
              <v-row dense class="mb-2">
                <v-col cols="9">
                  <v-text-field v-model="accountSearch.query" label="Search by name or TRN" dense outlined hide-details @keyup.enter="searchAccounts" />
                </v-col>
                <v-col cols="3">
                  <v-btn depressed color="primary" small :loading="searchingAccounts" style="height:40px;width:100%" @click="searchAccounts">
                    <v-icon small>mdi-magnify</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <div v-if="accountSearch.results.length">
                <v-list dense outlined style="border-radius:8px;overflow:hidden;max-height:200px;overflow-y:auto">
                  <v-list-item
                    v-for="r in accountSearch.results" :key="r.accountId"
                    dense style="cursor:pointer" @click="selectAccount(r)"
                  >
                    <v-list-item-icon class="mr-2"><v-icon small color="primary">mdi-account-circle-outline</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="caption font-weight-medium">{{ r.personName }}</v-list-item-title>
                      <v-list-item-subtitle class="caption">{{ r.mainAccountNumber }}<span v-if="r.trn"> · {{ r.trn }}</span></v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action><v-btn x-small depressed color="primary">Select</v-btn></v-list-item-action>
                  </v-list-item>
                </v-list>
              </div>
              <div v-if="accountSearch.searched && !accountSearch.results.length" class="caption grey--text text-center py-2">No results found.</div>
            </template>

            <v-divider class="my-4" />

            <!-- Names to search -->
            <div class="aml-section-label mb-2">Names to search</div>
            <div class="caption grey--text mb-2">Split first and last name into separate fields for best results.</div>
            <div v-for="(name, i) in singleForm.names" :key="i" class="d-flex align-center mb-2" style="gap:6px">
              <v-text-field v-model="singleForm.names[i]" :label="`Name ${i + 1}`" dense outlined hide-details />
              <v-btn icon small :disabled="singleForm.names.length <= 1" @click="removeName(i)">
                <v-icon small color="error">mdi-close-circle-outline</v-icon>
              </v-btn>
            </div>
            <v-btn x-small text color="primary" class="mb-4" @click="addName">
              <v-icon x-small left>mdi-plus</v-icon>Add name part
            </v-btn>
          </template>

          <!-- ── Mass Search ── -->
          <template v-else>
            <div class="aml-section-label mb-3">Mass screening</div>
            <div class="caption grey--text mb-4" style="line-height:1.6">
              Screens <strong>all active accounts</strong> in the system against every watchlist provider.
              Names are resolved automatically from account holder records — no manual input required.
              A session report is saved automatically on completion.
            </div>

            <!-- Optional scope filters -->
            <v-select
              v-model="massOptions.branchId"
              :items="[]"
              label="Branch filter (optional — leave empty for all branches)"
              dense outlined clearable
              prepend-inner-icon="mdi-office-building-outline"
              class="mb-3"
              no-data-text="All branches will be screened"
            />

            <!-- Last mass search info -->
            <div v-if="lastMassResult" class="aml-info-box mb-3">
              <v-icon small color="#185FA5" class="mr-2">mdi-information-outline</v-icon>
              <div>
                <div style="font-size:12px;font-weight:500">Last mass screening</div>
                <div class="aml-muted" style="font-size:11px;margin-top:2px">
                  {{ lastMassResult.totalAccounts }} accounts · {{ lastMassResult.possibleMatches }} possible matches ·
                  Session: <span style="font-family:monospace;font-size:10px">{{ lastMassResult.sessionId }}</span>
                </div>
              </div>
            </div>
          </template>

          <!-- ── Shared options ── -->
          <v-divider class="my-3" />
          <div class="aml-section-label mb-3">Search options</div>

          <v-select
            v-model="sharedOptions.sources"
            :items="providerOptions"
            item-text="text" item-value="value"
            label="Providers (leave empty to search all)"
            multiple chips small-chips deletable-chips
            dense outlined clearable
            prepend-inner-icon="mdi-database-outline"
            class="mb-3"
          >
            <template #selection="{ item }">
              <v-chip x-small close @click:close="removeSource(item.value)">{{ providerShort(item.value) }}</v-chip>
            </template>
          </v-select>

          <!-- Lists being screened against and how current each one is -->
          <div v-if="sources.length" class="aml-list-status mb-3">
            <div class="d-flex align-center justify-space-between mb-1">
              <span class="aml-section-label">Lists screened</span>
              <span v-if="sourcesNeedingAttention.length" class="aml-pill" style="background:#FFF4E5;color:#854F0B">
                {{ sourcesNeedingAttention.length }} need{{ sourcesNeedingAttention.length === 1 ? 's' : '' }} attention
              </span>
            </div>
            <div v-for="s in sources" :key="s.source_code" class="aml-list-status-row">
              <v-tooltip bottom max-width="300">
                <template #activator="{ on }">
                  <span v-on="on" class="d-flex align-center" style="gap:6px;min-width:0">
                    <v-icon x-small :color="s.needs_attention ? '#D85A30' : '#3B6D11'">
                      {{ s.needs_attention ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline' }}
                    </v-icon>
                    <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ s.label }}</span>
                  </span>
                </template>
                <span style="font-size:11px">
                  <template v-if="s.list_updated_at">List date: {{ describeListDate(s.list_updated_at, s.date_method) }}<br></template>
                  <template v-if="s.fetched_at">Last checked: {{ fmtDateTime(s.fetched_at) }}<br></template>
                  <template v-if="s.record_count">{{ s.record_count.toLocaleString() }} records<br></template>
                  <template v-if="s.attention_reason"><strong>{{ s.attention_reason }}</strong></template>
                </span>
              </v-tooltip>
              <span class="aml-muted" style="font-size:10px;flex-shrink:0">{{ isoDate(s.list_updated_at) || '—' }}</span>
            </div>
          </div>
          <v-select
            v-model="sharedOptions.data_sets"
            :items="dataSetOptions"
            item-text="text" item-value="value"
            label="Data sets (leave empty for all)"
            multiple chips small-chips deletable-chips
            dense outlined clearable
            class="mb-3"
          />
          <v-text-field
            v-model.number="sharedOptions.minimum_score"
            label="Minimum score (default 70)"
            type="number" dense outlined
            prepend-inner-icon="mdi-percent"
            class="mb-3"
          />
          <div class="d-flex" style="gap:12px">
            <v-switch v-model="sharedOptions.use_fuzzy"    label="Fuzzy matching"   dense hide-details class="mt-0" />
            <v-switch v-model="sharedOptions.search_alias" label="Search aliases"   dense hide-details class="mt-0" />
          </div>

          <div class="mt-4">
            <v-btn
              depressed color="primary"
              :loading="searching"
              :disabled="!canSearch"
              block
              @click="runSearch"
            >
              <v-icon left>{{ searchMode === 'mass' ? 'mdi-account-group-outline' : 'mdi-magnify' }}</v-icon>
              {{ searchMode === 'mass' ? 'Run mass screening' : 'Search' }}
            </v-btn>
          </div>
        </div>
      </v-col>

      <!-- ══ Results ══ -->
      <v-col cols="12" md="7">
        <div class="aml-card">

          <!-- No results yet -->
          <div v-if="!searchDone && !searching" class="aml-empty-results">
            <v-icon size="48" color="grey lighten-2">mdi-magnify</v-icon>
            <div class="mt-3" style="color:#bbb;font-size:13px">Run a search to see results</div>
          </div>

          <!-- Loading -->
          <div v-else-if="searching" class="aml-empty-results">
            <v-progress-circular indeterminate color="primary" size="36" />
            <div class="mt-3" style="color:#aaa;font-size:13px">
              {{ searchMode === 'mass' ? 'Running mass screening across all accounts — this may take a moment…' : 'Searching watchlists…' }}
            </div>
          </div>

          <!-- Results -->
          <template v-else-if="searchDone">

            <!-- ── Single result ── -->
            <template v-if="searchMode === 'single' && singleResult">

              <!-- Summary toolbar -->
              <div class="aml-toolbar">
                <span style="font-size:13px;font-weight:500">Search results</span>
                <div class="d-flex align-center" style="gap:6px">
                  <span
                    class="aml-pill"
                    :style="singleResult.possibleMatches > 0
                      ? 'background:#FCEBEB;color:#A32D2D'
                      : 'background:#EAF3DE;color:#3B6D11'"
                  >
                    {{ singleResult.possibleMatches }} possible match{{ singleResult.possibleMatches !== 1 ? 'es' : '' }}
                  </span>
                  <span class="aml-pill" style="background:#f5f5f5;color:#666">
                    {{ totalResultCount(singleResult) }} total result{{ totalResultCount(singleResult) !== 1 ? 's' : '' }}
                  </span>
                  <v-btn x-small depressed class="aml-btn-ghost" :loading="loadingMassReport" @click="printReport">
                    <v-icon x-small left>mdi-printer-outline</v-icon>Print report
                  </v-btn>
                </div>
              </div>

              <!-- ── Per-provider collapsible sections ── -->
              <div
                v-for="p in singleResult.providers"
                :key="p.provider"
                class="aml-provider-section"
                :class="p.matchStatus === 'POSSIBLE_MATCH' ? 'aml-provider-section--hit' : ''"
              >
                <div
                  class="aml-provider-head"
                  :class="p.matchStatus === 'POSSIBLE_MATCH' ? 'aml-provider-head--hit' : ''"
                  @click="toggleProvider(p.provider)"
                >
                  <div class="d-flex align-center" style="gap:8px">
                    <v-icon
                      small
                      :color="p.matchStatus === 'POSSIBLE_MATCH' ? '#A32D2D' : '#bbb'"
                      style="transition:transform 0.2s"
                      :style="isProviderExpanded(p.provider) ? 'transform:rotate(90deg)' : ''"
                    >mdi-chevron-right</v-icon>

                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <span v-on="on" class="aml-type-badge" :style="p.matchStatus === 'POSSIBLE_MATCH' ? 'background:#FCEBEB;color:#A32D2D;font-weight:700' : ''">
                          {{ providerShort(p.provider) }}
                        </span>
                      </template>
                      <span>{{ providerLabel(p.provider) }}</span>
                    </v-tooltip>
                    <span class="aml-pill" :style="matchStatusStyle(p.matchStatus)">
                      {{ matchStatusLabel(p.matchStatus) }}
                    </span>
                    <span v-if="p.matchCount" class="aml-muted" style="font-size:11px">
                      {{ p.matchCount }} result{{ p.matchCount !== 1 ? 's' : '' }}
                    </span>
                    <span v-if="p.list && p.list.list_updated_at" class="aml-muted" style="font-size:10px">
                      · list of {{ isoDate(p.list.list_updated_at) }}
                    </span>
                    <v-tooltip v-if="p.list && p.list.needs_attention" bottom max-width="280">
                      <template #activator="{ on }">
                        <v-icon x-small color="#D85A30" v-on="on">mdi-alert-circle-outline</v-icon>
                      </template>
                      <span style="font-size:11px">{{ p.list.attention_reason }}</span>
                    </v-tooltip>
                  </div>

                  <div class="d-flex align-center" style="gap:6px" @click.stop>
                    <!-- Account-linked: flag created -->
                    <v-btn
                      v-if="p.matchStatus === 'POSSIBLE_MATCH' && p.flagId"
                      x-small depressed color="primary"
                      @click="goToFlag(p.flagId)"
                    >
                      <v-icon x-small left>mdi-eye</v-icon>Review flag
                    </v-btn>
                    <!-- Suppressed: offer bypass option if account is linked -->
                    <v-btn
                      v-if="p.matchStatus === 'SUPPRESSED' && selectedAccount && p.flagId"
                      x-small depressed
                      color="orange darken-2"
                      dark
                      @click="openBypassDialog(p)"
                    >
                      <v-icon x-small left>mdi-refresh</v-icon>Force search
                    </v-btn>
                    <!-- Suppressed: show reason tooltip -->
                    <v-tooltip v-if="p.matchStatus === 'SUPPRESSED'" bottom max-width="260">
                      <template #activator="{ on }">
                        <v-icon x-small color="#aaa" v-on="on">mdi-information-outline</v-icon>
                      </template>
                      <span style="font-size:11px">{{ p.reason || 'This provider was skipped because a resolved or active flag already exists for this account.' }}</span>
                    </v-tooltip>
                  </div>
                </div>

                <!-- Collapsible body -->
                <div v-if="isProviderExpanded(p.provider) && p.matches && p.matches.length" class="aml-provider-body">

                  <!-- Hit cards -->
                  <div
                    v-for="(hit, hi) in visibleHits(p.provider, p.matches)"
                    :key="hi"
                    class="aml-hit-card"
                    :class="{ 'aml-hit-card--expanded': isHitExpanded(p.provider, hi) }"
                  >
                    <!-- Compact header -->
                    <div
                      class="aml-hit-compact"
                      @click="toggleHit(p.provider, hi)"
                    >
                      <div class="d-flex align-center" style="gap:8px;flex:1;min-width:0">
                        <v-icon x-small :color="isHitExpanded(p.provider, hi) ? '#185FA5' : '#bbb'" style="flex-shrink:0">
                          {{ isHitExpanded(p.provider, hi) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                        </v-icon>
                        <span class="aml-hit-name">{{ hit.name }}</span>
                        <div class="d-flex align-center" style="gap:4px;flex-shrink:0;flex-wrap:wrap">
                          <span class="aml-type-badge">{{ hit.source_code }}</span>
                          <span class="aml-type-badge">{{ hit.data_set }}</span>
                          <span class="aml-type-badge">{{ hit.type }}</span>
                        </div>
                      </div>
                      <span class="aml-score-pill" :style="scoreStyle(hit.score, hit.max_score)">
                        {{ hit.score }}
                        <span v-if="hit.max_score" style="opacity:0.6;font-size:9px"> / {{ hit.max_score }}</span>
                      </span>
                    </div>

                    <!-- Expanded detail -->
                    <div v-if="isHitExpanded(p.provider, hi)" class="aml-hit-detail">
                      <div class="aml-hit-detail-divider" />

                      <div v-if="hit.aliases" class="aml-detail-row">
                        <span class="aml-detail-label">Aliases</span>
                        <span class="aml-detail-val">{{ hit.aliases }}</span>
                      </div>

                      <div v-if="hit.address" class="aml-detail-row">
                        <span class="aml-detail-label">Address</span>
                        <span class="aml-detail-val">{{ hit.address }}</span>
                      </div>

                      <div class="aml-detail-row">
                        <span class="aml-detail-label">Source</span>
                        <span class="aml-detail-val">
                          {{ hit.source_name }}
                          <a
                            v-if="isSafeUrl(hit.source_url)"
                            :href="hit.source_url" target="_blank" rel="noopener noreferrer"
                            class="aml-source-link" @click.stop
                          >
                            <v-icon x-small color="#185FA5">mdi-open-in-new</v-icon>View at source
                          </a>
                        </span>
                      </div>

                      <div v-if="hit.last_updated" class="aml-detail-row">
                        <span class="aml-detail-label">List date</span>
                        <span class="aml-detail-val">{{ describeListDate(hit.last_updated, hit.date_method) }}</span>
                      </div>

                      <!-- Meta data key/value rows -->
                      <template v-if="hit.meta_data">
                        <div
                          v-for="(val, key) in flatMetaFields(hit.meta_data)"
                          :key="key"
                          class="aml-detail-row"
                        >
                          <span class="aml-detail-label">{{ formatMetaKey(key) }}</span>
                          <span class="aml-detail-val">{{ val }}</span>
                        </div>
                      </template>
                    </div>
                  </div>

                  <!-- Show more -->
                  <div v-if="p.matches.length > hitsPerPage" class="aml-show-more-row">
                    <span class="aml-muted" style="font-size:11px">
                      Showing {{ visibleHits(p.provider, p.matches).length }} of {{ p.matches.length }}
                    </span>
                    <div class="d-flex" style="gap:6px">
                      <button
                        v-if="providerPage(p.provider) > 1"
                        class="aml-show-more-btn"
                        @click="prevPage(p.provider)"
                      >
                        <v-icon x-small left>mdi-chevron-left</v-icon>Previous
                      </button>
                      <button
                        v-if="visibleHits(p.provider, p.matches).length < p.matches.length || providerPage(p.provider) > 1"
                        class="aml-show-more-btn"
                        :disabled="providerPage(p.provider) * hitsPerPage >= p.matches.length"
                        @click="nextPage(p.provider, p.matches.length)"
                      >
                        Next <v-icon x-small right>mdi-chevron-right</v-icon>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Error -->
                <div v-if="p.error" class="pa-3 caption red--text">
                  Error: {{ p.error }}
                </div>
              </div>

              <!-- Footer actions -->
              <div class="pa-3">
                <v-btn
                  v-if="singleResult.possibleMatches > 0 && !singleResult.adHoc"
                  depressed color="primary" small
                  @click="goToFlag(firstFlagId(singleResult))"
                >
                  <v-icon small left>mdi-file-search-outline</v-icon>Open flag detail
                </v-btn>
                <v-alert
                  v-else-if="singleResult.possibleMatches > 0 && singleResult.adHoc"
                  dense outlined type="info"
                  class="mb-0"
                  style="border-radius:8px;font-size:12px"
                >
                  Possible matches found. To open an investigation, link an account above and run the search again — a flag will be created automatically.
                </v-alert>
              </div>

            </template>

            <!-- ── Mass search result ── -->
            <template v-else-if="searchMode === 'mass' && massResult">
              <div class="aml-toolbar">
                <span style="font-size:13px;font-weight:500">Mass screening complete</span>
                <div class="d-flex align-center" style="gap:6px">
                  <span class="aml-pill" style="background:#EAF3DE;color:#3B6D11">
                    {{ massResult.totalAccounts }} accounts
                  </span>
                  <span
                    class="aml-pill"
                    :style="massResult.possibleMatches > 0 ? 'background:#FCEBEB;color:#A32D2D' : 'background:#EAF3DE;color:#3B6D11'"
                  >
                    {{ massResult.possibleMatches }} possible match{{ massResult.possibleMatches !== 1 ? 'es' : '' }}
                  </span>
                  <span v-if="massResult.errors > 0" class="aml-pill" style="background:#FFF4E5;color:#854F0B">
                    {{ massResult.errors }} error{{ massResult.errors !== 1 ? 's' : '' }}
                  </span>
                  <v-btn x-small depressed class="aml-btn-ghost" :loading="loadingMassReport" @click="printReport">
                    <v-icon x-small left>mdi-printer-outline</v-icon>Print report
                  </v-btn>
                </div>
              </div>

              <!-- Session info -->
              <div class="pa-3 pb-0">
                <div class="aml-info-box mb-3">
                  <v-icon small color="#185FA5" class="mr-2">mdi-check-circle-outline</v-icon>
                  <div>
                    <div style="font-size:12px;font-weight:500">Report saved</div>
                    <div class="aml-muted" style="font-size:11px;margin-top:2px">
                      Session <span style="font-family:monospace">{{ massResult.sessionId }}</span> — viewable in
                      <a style="color:#185FA5;cursor:pointer" @click="$router.push({ name: 'watchlist-search-reports' })">Search reports</a>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Hits only -->
              <div v-if="!massResult.hits || !massResult.hits.length" class="aml-empty-results" style="min-height:120px">
                <v-icon size="36" color="grey lighten-2">mdi-check-circle-outline</v-icon>
                <div class="mt-2" style="color:#bbb;font-size:13px">No possible matches found across all accounts</div>
              </div>

              <div
                v-for="r in massResult.hits"
                :key="r.accountId"
                class="aml-bulk-result-row aml-bulk-hit"
              >
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <span style="font-size:12px;font-weight:500">{{ r.mainAccountNumber }}</span>
                    <span class="aml-pill ml-2" style="background:#FCEBEB;color:#A32D2D">
                      {{ r.possibleMatches }} match{{ r.possibleMatches !== 1 ? 'es' : '' }}
                    </span>
                  </div>
                  <v-btn x-small depressed color="primary" @click="goToAccountFlags(r.accountId)">
                    <v-icon x-small left>mdi-eye</v-icon>View flags
                  </v-btn>
                </div>
                <div class="mt-2 d-flex flex-wrap" style="gap:3px">
                  <span
                    v-for="p in r.providers.filter(p => p.matchStatus === 'POSSIBLE_MATCH')"
                    :key="p.provider"
                    class="aml-provider-chip"
                    style="background:#FCEBEB;color:#A32D2D;border-color:#F09595"
                  >{{ providerShort(p.provider) }}</span>
                </div>
              </div>
            </template>

          </template>
        </div>
      </v-col>
    </v-row>

  </v-container>
  </v-fade-transition>

  <!-- ══ Force search bypass dialog ══ -->
  <v-dialog v-model="bypassDialog" max-width="480" persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon left color="orange darken-2">mdi-refresh</v-icon>
        <span class="subtitle-1 font-weight-medium">Force search on {{ bypassTarget && bypassTarget.provider }}</span>
        <v-spacer />
        <v-btn icon @click="bypassDialog = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-4">
        <v-alert dense outlined type="warning" class="mb-4" style="font-size:12px">
          This provider was skipped because a resolved flag already exists for <strong>{{ selectedAccount && selectedAccount.mainAccountNumber }}</strong> on <strong>{{ bypassTarget && bypassTarget.provider }}</strong>.
          Forcing a re-search will set an override on that flag and run a new search immediately.
        </v-alert>
        <v-textarea
          v-model="bypassReason"
          label="Reason for re-search *"
          rows="3"
          dense outlined
          :rules="[v => !!v || 'A reason is required']"
        />
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="bypassDialog = false">Cancel</v-btn>
        <v-btn
          depressed color="orange darken-2" dark
          :loading="bypassing"
          :disabled="!bypassReason.trim()"
          @click="submitBypass"
        >
          <v-icon left small>mdi-refresh</v-icon>Force re-search
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Single search print report -->
  <WatchlistPrintReport
    v-if="searchDone && singleResult"
    :result="singleResult"
    :meta="printMeta"
    :account="selectedAccount"
    :options="sharedOptions"
    :names="singleForm.names"
  />

  <!-- Mass search print report -->
  <WatchlistReportPrint
    v-if="massReportDetail"
    :report="massReportDetail"
  />

  </div>
</template>

<script>
import { mapState }        from 'vuex';
import PageMixin           from '@/mixins/page.mixin';
import FormatMixin         from '@/mixins/format.mixin';
import AmlMixin            from '@/mixins/aml.mixin';
import { WatchlistService }    from '@/modules/amlpro/services/watchlist.service';
import { MemberProfileService } from '@/modules/amlpro/services/member-profile-service';
import WatchlistPrintReport     from './WatchlistPrintReport.vue';
import {
  DATA_SET_OPTIONS, HIDDEN_META_KEYS, providerItems, providerLabel, providerShort,
  describeListDate, isoDate, isSafeUrl,
} from '@/modules/amlpro/services/watchlist-sources';
import WatchlistReportPrint     from './WatchlistReportPrint.vue';

const svc       = new WatchlistService();
const memberSvc = new MemberProfileService();

const freshBulkEntry = () => ({
  accountId:       null,
  selectedAccount: null,
  names:           [''],
  searchQuery:     '',
  searchResults:   [],
  searching:       false,
});

export default {
  name: 'WatchlistSearch',
  mixins: [PageMixin, FormatMixin, AmlMixin],
  components: { WatchlistPrintReport, WatchlistReportPrint },

  data() {
    return {
      searchMode:        this.$route.query.mode === 'mass' ? 'mass' : 'single',
      searching:        false,
      searchDone:       false,
      searchingAccounts: false,

      // Single search
      selectedAccount:  null,
      accountSearch:    { query: '', results: [], searched: false },
      singleForm:       { names: [''] },
      singleResult:     null,

      // Mass search
      massResult:       null,
      lastMassResult:   null,
      massOptions:      { branchId: null },
      massReportDetail: null,
      loadingMassReport: false,

      // Shared options
      sharedOptions: {
        sources:       [],
        data_sets:     [],
        minimum_score: 70,
        use_fuzzy:     false,
        search_alias:  false,
      },

      sources:          [],
      dataSetOptions:   DATA_SET_OPTIONS,

      hitsPerPage:       5,
      expandedProviders: {},
      expandedHits:      {},
      providerPages:     {},   

      printMeta: { date: '', time: '', officer: '' },

      bypassDialog: false,
      bypassTarget: null, 
      bypassReason: '',
      bypassing:    false,
    };
  },

  computed: {
    ...mapState('authModule', { loggedInUser: 'user' }),

    canSearch() {
      if (this.searchMode === 'mass') return true;
      return this.singleForm.names.some(n => n.trim());
    },

    providerOptions() {
      return providerItems(this.sources);
    },

    sourcesNeedingAttention() {
      return this.sources.filter(s => s.needs_attention);
    },
  },

  mounted() {
    this.loadSources();
  },

  methods: {
    providerLabel, providerShort, describeListDate, isoDate, isSafeUrl,

    async loadSources() {
      try {
        const res    = await svc.fetchSources();
        const data   = res.data ?? res;
        this.sources = Array.isArray(data) ? data : [];
      } catch {
        this.sources = [];
      }
    },

    removeSource(code) {
      this.sharedOptions.sources = this.sharedOptions.sources.filter(s => s !== code);
    },

    fmtDateTime(v) {
      if (!v) return '';
      const d = new Date(v);
      return isNaN(d) ? String(v) : d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },

    async searchAccounts() {
      if (!this.accountSearch.query.trim()) return;
      this.searchingAccounts      = true;
      this.accountSearch.results  = [];
      this.accountSearch.searched = false;
      try {
        const q       = this.accountSearch.query.trim();
        const filters = q.match(/^\d/) ? { trn: q } : { firstName: q };
        const res     = await memberSvc.searchMembers(filters, 0, 20);
        const data    = res.data ?? res;
        const persons = data.data ?? data;
        const results = [];
        for (const p of persons) {
          for (const pta of (p.accounts ?? [])) {
            const a = pta.account ?? pta;
            results.push({
              accountId: a.id,
              mainAccountNumber: a.mainAccountNumber,
              personName: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
              trn: p.trn ?? '',
              names: [p.firstName ?? '', p.lastName ?? ''].filter(Boolean),
            });
          }
        }
        this.accountSearch.results  = results;
        this.accountSearch.searched = true;
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Account search failed' });
      } finally {
        this.searchingAccounts = false;
      }
    },

    selectAccount(r) {
      this.selectedAccount       = r;
      this.singleForm.names      = r.names.length ? r.names : [''];
      this.accountSearch.results = [];
    },

    clearAccount() {
      this.selectedAccount       = null;
      this.singleForm.names      = [''];
      this.accountSearch.query   = '';
      this.accountSearch.results = [];
      this.singleResult          = null;
      this.searchDone            = false;
    },

    async searchBulkAccount(i) {
      const entry = this.bulkEntries[i];
      if (!entry.searchQuery.trim()) return;
      this.$set(entry, 'searching', true);
      try {
        const q       = entry.searchQuery.trim();
        const filters = q.match(/^\d/) ? { trn: q } : { firstName: q };
        const res     = await memberSvc.searchMembers(filters, 0, 10);
        const data    = res.data ?? res;
        const persons = data.data ?? data;
        const results = [];
        for (const p of persons) {
          for (const pta of (p.accounts ?? [])) {
            const a = pta.account ?? pta;
            results.push({
              accountId: a.id,
              mainAccountNumber: a.mainAccountNumber,
              personName: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
              names: [p.firstName ?? '', p.lastName ?? ''].filter(Boolean),
            });
          }
        }
        this.$set(entry, 'searchResults', results);
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Search failed' });
      } finally {
        this.$set(entry, 'searching', false);
      }
    },

    selectBulkAccount(i, r) {
      const entry = this.bulkEntries[i];
      this.$set(entry, 'selectedAccount', r);
      this.$set(entry, 'accountId', r.accountId);
      this.$set(entry, 'names', r.names.length ? r.names : ['']);
      this.$set(entry, 'searchResults', []);
    },

    clearBulkAccount(i) {
      const entry = this.bulkEntries[i];
      this.$set(entry, 'selectedAccount', null);
      this.$set(entry, 'accountId', null);
      this.$set(entry, 'names', ['']);
      this.$set(entry, 'searchQuery', '');
    },

    addBulkEntry()        { this.bulkEntries.push(freshBulkEntry()); },
    removeBulkEntry(i)    { this.bulkEntries.splice(i, 1); },
    addBulkName(i)        { this.bulkEntries[i].names.push(''); },
    removeBulkName(i, ni) { this.bulkEntries[i].names.splice(ni, 1); },

    addName()     { this.singleForm.names.push(''); },
    removeName(i) { this.singleForm.names.splice(i, 1); },

    async runSearch() {
      this.searching         = true;
      this.searchDone        = false;
      this.singleResult      = null;
      this.massResult        = null;
      this.massReportDetail  = null;
      this.expandedProviders = {};
      this.expandedHits      = {};
      this.providerPages     = {};
      try {
        if (this.searchMode === 'mass') {
          const payload = {
            ...this.cleanOptions(),
            ...(this.massOptions.branchId ? { branchId: this.massOptions.branchId } : {}),
          };
          const res           = await svc.massSearch(payload);
          this.massResult     = res.data ?? res;
          this.lastMassResult = this.massResult;
        } else {
          const payload = {
            names:         this.singleForm.names.filter(n => n.trim()),
            ...this.cleanOptions(),
            triggerReason: 'Manual search',
          };
          if (this.selectedAccount) {
            payload.accountId = this.selectedAccount.accountId;
          }
          const res         = await svc.manualSearch(payload);
          this.singleResult = res.data ?? res;
        }
        this.searchDone = true;

        const now = new Date();
        this.printMeta = {
          date:    now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
          time:    now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
          officer: this.loggedInUser
            ? ((this.loggedInUser.person
                ? `${this.loggedInUser.person.firstName ?? ''} ${this.loggedInUser.person.lastName ?? ''}`.trim()
                : '') || this.loggedInUser.username || 'Unknown officer')
            : 'Unknown officer',
        };

        if (this.singleResult && this.singleResult.providers) {
          const expanded = {};
          for (const p of this.singleResult.providers) {
            if (p.matchCount > 0 && p.matchCount <= this.hitsPerPage) {
              expanded[p.provider] = true;
            }
          }
          this.expandedProviders = expanded;
        }
      } catch (e) {
        const msg = e?.response?.data?.message ?? 'Search failed';
        this.showSnack({ color: 'error', title: 'Error', message: Array.isArray(msg) ? msg.join(', ') : msg });
      } finally {
        this.searching = false;
      }
    },

    cleanOptions() {
      const o = { ...this.sharedOptions };
      if (!o.sources?.length)   delete o.sources;
      if (!o.data_sets?.length) delete o.data_sets;
      if (!o.minimum_score)     delete o.minimum_score;
      return o;
    },

    firstFlagId(result) {
      const hit = result.providers?.find(p => p.flagId);
      return hit?.flagId ?? null;
    },

    openBypassDialog(provider) {
      this.bypassTarget = provider;
      this.bypassReason = '';
      this.bypassDialog = true;
    },

    async submitBypass() {
      if (!this.bypassReason.trim() || !this.bypassTarget?.flagId) return;
      this.bypassing = true;
      try {
        // Step 1: set allowResearch = true on the flag
        await svc.setResearchOverride(this.bypassTarget.flagId, this.bypassReason);

        // Step 2: re-run the search scoped to just this provider
        const payload = {
          names:         this.singleForm.names.filter(n => n.trim()),
          sources:       [this.bypassTarget.provider],
          ...this.cleanOptions(),
          triggerReason: `Force re-search override: ${this.bypassReason}`,
        };
        if (this.selectedAccount) payload.accountId = this.selectedAccount.accountId;

        const res    = await svc.manualSearch(payload);
        const result = res.data ?? res;

        // Step 3: merge the new provider result into the existing singleResult
        if (this.singleResult && result.providers) {
          const updated = [...this.singleResult.providers];
          for (const newP of result.providers) {
            const idx = updated.findIndex(p => p.provider === newP.provider);
            if (idx !== -1) updated.splice(idx, 1, newP);
            else updated.push(newP);
          }
          this.singleResult = {
            ...this.singleResult,
            providers:      updated,
            possibleMatches: updated.filter(p => p.matchStatus === 'POSSIBLE_MATCH').length,
          };
          if (result.providers[0]?.matchCount > 0) {
            this.$set(this.expandedProviders, this.bypassTarget.provider, true);
          }
        }

        this.showSnack({
          color:   result.providers?.[0]?.matchStatus === 'POSSIBLE_MATCH' ? 'warning' : 'success',
          title:   result.providers?.[0]?.matchStatus === 'POSSIBLE_MATCH' ? 'Possible match found' : 'No match',
          message: `Re-search on ${this.bypassTarget.provider} complete`,
        });
        this.bypassDialog = false;
      } catch (e) {
        const msg = e?.response?.data?.message ?? 'Re-search failed';
        this.showSnack({ color: 'error', title: 'Error', message: Array.isArray(msg) ? msg.join(', ') : msg });
      } finally {
        this.bypassing = false;
      }
    },

    async printReport() {
      if (this.searchMode === 'mass' && this.massResult?.sessionId) {
        if (!this.massReportDetail || this.massReportDetail.sessionId !== this.massResult.sessionId) {
          this.loadingMassReport = true;
          try {
            const res              = await svc.fetchSearchReport(this.massResult.sessionId);
            this.massReportDetail  = res.data ?? res;
          } catch {
            this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load report for printing' });
            return;
          } finally {
            this.loadingMassReport = false;
          }
        }
        await this.$nextTick();
      }
      window.print();
    },

    goToFlag(flagId)        { if (flagId) this.$router.push({ name: 'watchlist-flag-detail', params: { flagId } }); },
    goToAccountFlags(accountId) { this.$router.push({ name: 'watchlist-dashboard', query: { accountId } }); },

    toggleProvider(provider) {
      this.$set(this.expandedProviders, provider, !this.expandedProviders[provider]);
      // Collapse all hit expansions for this provider when collapsing
      if (!this.expandedProviders[provider]) {
        const updated = { ...this.expandedHits };
        Object.keys(updated).forEach(k => {
          if (k.startsWith(provider + '_')) delete updated[k];
        });
        this.expandedHits = updated;
      }
    },

    isProviderExpanded(provider) {
      return !!this.expandedProviders[provider];
    },

    toggleHit(provider, index) {
      const key = `${provider}_${index}`;
      this.$set(this.expandedHits, key, !this.expandedHits[key]);
    },

    isHitExpanded(provider, index) {
      return !!this.expandedHits[`${provider}_${index}`];
    },

    providerPage(provider) {
      return this.providerPages[provider] ?? 1;
    },

    visibleHits(provider, matches) {
      const page  = this.providerPage(provider);
      const start = (page - 1) * this.hitsPerPage;
      const end   = start + this.hitsPerPage;
      return matches.slice(start, end);
    },

    nextPage(provider, total) {
      const current = this.providerPage(provider);
      if (current * this.hitsPerPage < total) {
        this.$set(this.providerPages, provider, current + 1);
        // Collapse all hit expansions when turning page
        const updated = { ...this.expandedHits };
        Object.keys(updated).forEach(k => {
          if (k.startsWith(provider + '_')) delete updated[k];
        });
        this.expandedHits = updated;
      }
    },

    prevPage(provider) {
      const current = this.providerPage(provider);
      if (current > 1) {
        this.$set(this.providerPages, provider, current - 1);
        const updated = { ...this.expandedHits };
        Object.keys(updated).forEach(k => {
          if (k.startsWith(provider + '_')) delete updated[k];
        });
        this.expandedHits = updated;
      }
    },

    scoreStyle(score, maxScore) {
      if (!maxScore) return 'background:#f5f5f5;color:#888';
      const pct = (score / maxScore) * 100;
      if (pct >= 80) return 'background:#FCEBEB;color:#A32D2D';
      if (pct >= 50) return 'background:#FFF4E5;color:#854F0B';
      return 'background:#f5f5f5;color:#888';
    },

    totalResultCount(result) {
      if (!result || !result.providers) return 0;
      return result.providers.reduce((sum, p) => sum + (p.matchCount ?? 0), 0);
    },

    flatMetaFields(meta) {
      if (!meta || typeof meta !== 'object') return {};
      const skip = ['aliases', 'addresses', 'vessel_info', 'images', 'files',
                    'field_offices', 'locations', 'names', 'possible_countries',
                    'possible_states', 'program_list', 'occupations', ...HIDDEN_META_KEYS];
      const out = {};
      for (const [k, v] of Object.entries(meta)) {
        if (skip.includes(k)) continue;
        if (v === null || v === undefined || v === '') continue;
        if (typeof v === 'object') continue;
        out[k] = v;
      }
      return out;
    },

    formatMetaKey(key) {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
    },

    matchStatusLabel(s) {
      const map = { POSSIBLE_MATCH: 'Possible match', NO_MATCH: 'No match', SUPPRESSED: 'Suppressed', ERROR: 'Error' };
      return map[s] ?? s;
    },

    matchStatusStyle(s) {
      const map = {
        POSSIBLE_MATCH: 'background:#FCEBEB;color:#A32D2D',
        NO_MATCH:       'background:#EAF3DE;color:#3B6D11',
        SUPPRESSED:     'background:#F1EFE8;color:#5F5E5A',
        ERROR:          'background:#FFF4E5;color:#854F0B',
      };
      return map[s] ?? 'background:#f5f5f5;color:#888';
    },

    avatarInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
    },
  },
};
</script>

<style scoped>
.aml-search-root { }
.aml-dash        { background:transparent; }
.aml-overline    { font-size:11px; font-weight:600; letter-spacing:1px; text-transform:uppercase; color:#999; }
.aml-title       { font-size:22px; font-weight:500; letter-spacing:-0.3px; margin:0; }
.aml-back-btn    { width:34px; height:34px; border-radius:8px; border:0.5px solid #e0e0e0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background 0.12s; flex-shrink:0; }
.aml-back-btn:hover { background:#f5f5f5; }
.aml-card        { border:0.5px solid #e0e0e0; border-radius:12px; overflow:hidden; background:white; }
.aml-section-label { font-size:10px; font-weight:600; letter-spacing:.5px; text-transform:uppercase; color:#aaa; }
.aml-person-pill { display:flex; align-items:center; gap:10px; background:#EBF3FC; border-radius:10px; padding:10px 12px; }
.aml-person-avatar { width:36px; height:36px; border-radius:50%; background:#185FA5; color:white; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; font-family:monospace; flex-shrink:0; }
.aml-person-name { font-size:13px; font-weight:500; }
.aml-person-sub  { font-size:11px; color:#888; margin-top:2px; }
.aml-toolbar     { padding:12px 16px; border-bottom:0.5px solid #e0e0e0; display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; }
.aml-muted       { color:#777; }
.aml-type-badge  { font-size:10px; font-weight:700; color:#666; background:#f5f5f5; padding:2px 6px; border-radius:4px; }
.aml-pill        { display:inline-block; padding:2px 8px; border-radius:20px; font-size:10px; font-weight:500; white-space:nowrap; }
.aml-empty-results { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:240px; padding:32px; }
/* ── Provider sections ── */
.aml-provider-section         { border-bottom:0.5px solid #f0f0f0; }
.aml-provider-section:last-child { border-bottom:none; }
.aml-provider-section--hit    { background:#FFFAFA; }
.aml-provider-head            { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; cursor:pointer; user-select:none; transition:background 0.1s; }
.aml-provider-head:hover      { background:#f7f9fc; }
.aml-provider-head--hit:hover { background:#FFF0F0; }
.aml-provider-body            { padding:0 16px 14px; display:flex; flex-direction:column; gap:6px; }

/* ── Hit cards ── */
.aml-hit-card                 { border:0.5px solid #e8e8e8; border-radius:10px; overflow:hidden; background:white; transition:border-color 0.12s; }
.aml-hit-card--expanded       { border-color:#B5D4F4; }
.aml-hit-compact              { display:flex; align-items:center; justify-content:space-between; gap:10px; padding:10px 12px; cursor:pointer; transition:background 0.1s; }
.aml-hit-compact:hover        { background:#f7f9fc; }
.aml-hit-name                 { font-size:12px; font-weight:600; flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.aml-score-pill               { font-size:10px; font-weight:700; padding:2px 8px; border-radius:20px; white-space:nowrap; flex-shrink:0; }
.aml-hit-detail               { padding:0 12px 12px; }
.aml-hit-detail-divider       { height:0.5px; background:#e8e8e8; margin-bottom:10px; }
.aml-detail-row               { display:flex; gap:12px; padding:3px 0; font-size:11px; }
.aml-detail-label             { color:#999; min-width:110px; flex-shrink:0; }
.aml-detail-val               { color:#444; word-break:break-word; }

/* ── Show more pagination ── */
.aml-show-more-row            { display:flex; align-items:center; justify-content:space-between; padding:8px 4px 4px; }
.aml-show-more-btn            { font-size:11px; padding:3px 10px; border-radius:6px; border:0.5px solid #ddd; background:transparent; cursor:pointer; font-family:inherit; color:#185FA5; display:inline-flex; align-items:center; gap:2px; transition:background 0.1s; }
.aml-show-more-btn:hover      { background:#EBF3FC; }
.aml-show-more-btn:disabled   { color:#bbb; cursor:default; border-color:#eee; }

/* ── Bulk results ── */
.aml-bulk-result-row { padding:12px 16px; border-bottom:0.5px solid #f0f0f0; }
.aml-bulk-result-row:last-child { border-bottom:none; }
.aml-bulk-hit        { background:#FFF8F8; }
.aml-provider-chip   { font-size:10px; font-weight:500; padding:2px 8px; border-radius:20px; border:0.5px solid; display:inline-flex; align-items:center; gap:3px; }
.aml-bulk-entry      { border:0.5px solid #e0e0e0; border-radius:10px; padding:12px; background:#fafafa; }

.aml-list-status     { border:0.5px solid #e8e8e8; border-radius:8px; padding:8px 10px; background:#fafafa; }
.aml-list-status-row { display:flex; align-items:center; justify-content:space-between; gap:8px; font-size:11px; padding:2px 0; }
.aml-source-link     { margin-left:8px; color:#185FA5; text-decoration:none; font-size:11px; display:inline-flex; align-items:center; gap:2px; }
.aml-source-link:hover { text-decoration:underline; }

.aml-meta-row    { display:flex; gap:8px; padding:2px 0; font-size:11px; }
.aml-meta-key    { color:#999; min-width:130px; flex-shrink:0; text-transform:capitalize; }
.aml-meta-val    { color:#444; word-break:break-word; }

</style>